import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AuthLayout from '../../../shared/components/layout/AuthLayout';
import { useAuth } from '../../auth/hooks/useAuth';
import { fetchFriends, getUserProfile, sendFriendRequest } from '../services/friendsApi';
import { ConnectModal } from '../components/ConnectModal';
import ImageViewer from '../../../shared/components/ui/ImageViewer';
import { useChat } from '../../chat/context/ChatProvider';
import { FriendshipTier } from '../types/friends';
import { getTierVisibilityConfig, friendshipTierToViewTier } from '../../../shared/utils/tierVisibility';
import { CacheKeys } from '../../../shared/utils/cacheKeys';

// Import DashboardContext for friend data
import { useDashboardData } from '../../portfolio/hooks/useDashboardData';
import { DashboardProvider } from '../../portfolio/context/DashboardContext';
import { PortfolioViewProvider, usePortfolioView } from '../../portfolio/context/PortfolioViewContext';
import {
  selectPortfolioChart,
  selectAccountChart,
  selectActivityEvents,
  selectInvestmentsTable,
} from '../../portfolio/state/dashboardSelectors';
import type { TimeWindow } from '../../portfolio/services/dashboardApi';
import { useTickerChart } from '../../portfolio/hooks/useTickerChart';
import { getTickerMarket } from '../../portfolio/services/tickerApi';
import { getWeekStartMonday } from '../../../shared/utils/dateHelpers';

// Import card components
import {
  PortfolioSummaryCard,
  TopMoversCard,
  PerformanceChartCard,
  InvestmentTableCard,
} from '../../portfolio/components/cards';
import TradingActivityModal from '../../../shared/charts/TradingActivityModal';
import { TradingActivity } from '../../portfolio/types/tradingActivity';

interface FriendProfile {
  friendId: string;
  name: string;
  username: string;
  profilePictureUrl?: string;
  tierFromFriendToMe: FriendshipTier;
}

const TIER_COLORS: Record<FriendshipTier, string> = {
  'TIER_1': 'text-yellow-500',
  'TIER_2': 'text-gray-400',
  'TIER_3': '', // Custom color #A75217 applied via inline style
  'BASIC': 'text-gray-300'
};

const TIER_LABELS: Record<FriendshipTier, string> = {
  'TIER_1': 'Gold tier',
  'TIER_2': 'Silver tier',
  'TIER_3': 'Bronze tier',
  'BASIC': 'Basic tier'
};

export const FriendPortfolioPage: React.FC = () => {
  return (
    <AuthLayout>
      <PortfolioViewProvider>
        <FriendPortfolioPageInner />
      </PortfolioViewProvider>
    </AuthLayout>
  );
};

const FriendPortfolioPageInner: React.FC = () => {
  const { friendId } = useParams<{ friendId: string }>();
  const { session, sessionEstablishing } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { conversations } = useChat();
  
  // Get time window from context
  const { selectedTimeWindow, setSelectedTimeWindow, timeWindowOptions } = usePortfolioView();
  const timeWindowKey = selectedTimeWindow.key as TimeWindow;
  
  // Tooltip state for tier star
  const [showTierTooltip, setShowTierTooltip] = useState(false);
  const tierTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const starRef = useRef<HTMLSpanElement>(null);

  // Profile image viewer (friend and non-friend)
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);

  // Non-friend: Connect modal
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);

  // Trading Activity Modal state
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [selectedDayActivities, setSelectedDayActivities] = useState<TradingActivity[]>([]);
  const [selectedActivityDate, setSelectedActivityDate] = useState<string>('');
  
  const [isTimeWindowOpen, setIsTimeWindowOpen] = useState(false);
  const timeWindowRef = useRef<HTMLDivElement>(null);
  
  // Sticky time window selector state
  const [showStickyTimeWindow, setShowStickyTimeWindow] = useState(false);
  const [isStickyTimeWindowOpen, setIsStickyTimeWindowOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Selection state for Gold (TIER_1) and Silver (TIER_2) tiers
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);

  // Expanded accounts - only for Gold tier (TIER_1)
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(() => {
    if (!friendId || typeof window === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(CacheKeys.friendDashboard.expandedAccounts());
      if (!raw) return new Set();
      const data = JSON.parse(raw) as Record<string, string[]>;
      const accountIds = data[friendId] || [];
      return new Set(accountIds);
    } catch (error) {
      console.error('Error loading expanded accounts from localStorage:', error);
      return new Set();
    }
  });

  // Load selections when friendId changes (after friendProfile is loaded)
  useEffect(() => {
    if (!friendId || typeof window === 'undefined') return;
    
    // Load selectedAccountId
    try {
      const raw = localStorage.getItem(CacheKeys.friendDashboard.selectedAccountId());
      if (raw) {
        const data = JSON.parse(raw) as Record<string, string>;
        const accountId = data[friendId] || null;
        setSelectedAccountId(accountId);
      }
    } catch (error) {
      console.error('Error loading selected account ID from localStorage:', error);
    }

    // Load selectedTicker
    try {
      const raw = localStorage.getItem(CacheKeys.friendDashboard.selectedTicker());
      if (raw) {
        const data = JSON.parse(raw) as Record<string, string>;
        const ticker = data[friendId] || null;
        setSelectedTicker(ticker);
      }
    } catch (error) {
      console.error('Error loading selected ticker from localStorage:', error);
    }

    // Load expandedAccounts
    try {
      const raw = localStorage.getItem(CacheKeys.friendDashboard.expandedAccounts());
      if (raw) {
        const data = JSON.parse(raw) as Record<string, string[]>;
        const accountIds = data[friendId] || [];
        const newSet = new Set(accountIds);
        setExpandedAccounts(newSet);
      }
    } catch (error) {
      console.error('Error loading expanded accounts from localStorage:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendId]); // Only run when friendId changes, not when state variables change

  // Save selections to localStorage when they change
  useEffect(() => {
    if (!friendId || typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(CacheKeys.friendDashboard.selectedAccountId());
      const data = raw ? (JSON.parse(raw) as Record<string, string>) : {};
      if (selectedAccountId) {
        data[friendId] = selectedAccountId;
      } else {
        delete data[friendId];
      }
      localStorage.setItem(CacheKeys.friendDashboard.selectedAccountId(), JSON.stringify(data));
    } catch (error) {
      console.error('Error saving selected account ID to localStorage:', error);
    }
  }, [selectedAccountId, friendId]);

  useEffect(() => {
    if (!friendId || typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(CacheKeys.friendDashboard.selectedTicker());
      const data = raw ? (JSON.parse(raw) as Record<string, string>) : {};
      if (selectedTicker) {
        data[friendId] = selectedTicker;
      } else {
        delete data[friendId];
      }
      localStorage.setItem(CacheKeys.friendDashboard.selectedTicker(), JSON.stringify(data));
    } catch (error) {
      console.error('Error saving selected ticker to localStorage:', error);
    }
  }, [selectedTicker, friendId]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (timeWindowRef.current && !timeWindowRef.current.contains(event.target as Node)) {
        setIsTimeWindowOpen(false);
      }
    };

    if (isTimeWindowOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTimeWindowOpen]);

  // Scroll listener for sticky time window selector
  // Track the right edge position of the content container for alignment
  const [stickyRightOffset, setStickyRightOffset] = useState(16); // Default to 16px (1rem)
  
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerRect = headerRef.current.getBoundingClientRect();
        // Show sticky version when the header has scrolled past the top (with a small buffer)
        setShowStickyTimeWindow(headerRect.bottom < 0);
      }
      
      // Calculate right offset to align with content area
      if (contentRef.current) {
        const contentRect = contentRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const rightEdge = contentRect.right;
        const offset = viewportWidth - rightEdge;
        setStickyRightOffset(Math.max(16, offset)); // Minimum 16px padding
      }
    };

    // Run on scroll and resize
    handleScroll(); // Initial calculation
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Fetch friend profile using React Query to share cache with FriendsPage
  const { data: friendsList, isLoading: loading } = useQuery({
    queryKey: ['friends', timeWindowKey],
    queryFn: () => fetchFriends(session!.access_token, timeWindowKey),
    enabled: !!session?.access_token && !sessionEstablishing,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  // Extract the specific friend from the cached friends list
  const friendProfile = useMemo<FriendProfile | null>(() => {
    if (!friendsList || !friendId) return null;
    const friend = friendsList.find(f => f.friendId === friendId);
    if (!friend) return null;
    
    return {
      friendId: friend.friendId,
      name: friend.name,
      username: friend.username,
      profilePictureUrl: friend.profilePictureUrl || undefined,
      tierFromFriendToMe: friend.tierFromFriendToMe,
    };
  }, [friendsList, friendId]);

  // For non-friends: fetch user profile (name, image, request status)
  const { data: userProfile, isLoading: userProfileLoading } = useQuery({
    queryKey: ['userProfile', friendId],
    queryFn: () => getUserProfile(session!.access_token, friendId!),
    enabled: !!session?.access_token && !sessionEstablishing && !!friendId && !loading && friendProfile === null,
    staleTime: 60000,
  });

  // If we fetched a non-friend profile and it says isFriend, invalidate friends so we can show the friend view
  useEffect(() => {
    if (userProfile?.isFriend && friendId) {
      queryClient.invalidateQueries({ queryKey: ['friends', timeWindowKey] });
    }
  }, [userProfile?.isFriend, friendId, queryClient, timeWindowKey]);

  // Fetch friend dashboard data using React Query
  const dashboardData = useDashboardData({
    mode: 'friend',
    friendId: friendId || undefined,
    window: timeWindowKey,
    enabled: !!session?.access_token && !!friendId && friendProfile?.tierFromFriendToMe !== 'BASIC',
  });

  // Save expandedAccounts to localStorage when they change (after friendProfile is available)
  useEffect(() => {
    if (!friendId || typeof window === 'undefined') return;
    // Only save expanded accounts for Gold tier (TIER_1)
    if (!friendProfile) return;
    const viewTier = friendshipTierToViewTier(friendProfile.tierFromFriendToMe);
    if (viewTier !== 'tier1') return;
    
    try {
      const raw = localStorage.getItem(CacheKeys.friendDashboard.expandedAccounts());
      const data = raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
      const accountIds = Array.from(expandedAccounts);
      if (accountIds.length > 0) {
        data[friendId] = accountIds;
      } else {
        delete data[friendId];
      }
      localStorage.setItem(CacheKeys.friendDashboard.expandedAccounts(), JSON.stringify(data));
    } catch (error) {
      console.error('Error saving expanded accounts to localStorage:', error);
    }
  }, [expandedAccounts, friendId, friendProfile]);

  // Get tier visibility configuration
  const viewTier = friendProfile ? friendshipTierToViewTier(friendProfile.tierFromFriendToMe) : 'basic';
  const isGoldTier = viewTier === 'tier1';
  const visibilityConfig = getTierVisibilityConfig(viewTier);

  // Get data from DashboardContext (provided by DashboardProvider below)
  const { data } = dashboardData;

  // Compute chart data using selectors (same as PortfolioPage)
  const portfolioChart = useMemo(
    () => selectPortfolioChart(data, timeWindowKey),
    [data, timeWindowKey]
  );

  const accountChart = useMemo(
    () => selectedAccountId && data
      ? selectAccountChart(data, selectedAccountId, timeWindowKey)
      : { series: [], headerPct: null, headerDollar: null, view: 'pct' },
    [data, selectedAccountId, timeWindowKey]
  );

  // Fetch shared ticker chart data (market price series) from lazy endpoint
  const { data: tickerChartResponse, isLoading: isLoadingTickerChart } = useTickerChart(
    selectedTicker,
    timeWindowKey
  );
  
  // Fetch ticker market data for the selected ticker (to get company_name for chart title)
  const { data: selectedTickerMarketData } = useQuery({
    queryKey: ['ticker-market', selectedTicker],
    queryFn: async () => {
      if (!selectedTicker || !session?.access_token) {
        return null;
      }
      return getTickerMarket({
        ticker: selectedTicker,
        token: session.access_token,
      });
    },
    enabled: !!selectedTicker && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend TTL)
  });
  
  // Convert lazy ticker chart data to format expected by chart component
  const tickerChart = useMemo(() => {
    if (!selectedTicker) {
      return { priceSeries: [], name: null };
    }
    
    // If loading, return empty (will show loading state)
    if (isLoadingTickerChart || !tickerChartResponse?.data?.tickerCharts) {
      return { priceSeries: [], name: selectedTicker };
    }
    
    const tickerUpper = selectedTicker.toUpperCase();
    const tickerData = tickerChartResponse.data.tickerCharts[tickerUpper];
    const window = timeWindowKey;
    const windowSeries = tickerData?.[window];
    
    if (!windowSeries || !Array.isArray(windowSeries)) {
      return { priceSeries: [], name: selectedTicker };
    }
    
    // Convert to {time, close} format
    // Backend returns {date (epoch ms), price} format
    let priceSeries = windowSeries.map((point: any) => ({
      time: typeof point.date === 'number' ? point.date : (typeof point.date === 'string' ? new Date(point.date).getTime() : 0),
      close: point.price || 0,
    }));
    
    // For 1Y and 2Y, reduce to weekly buckets (Monday labels with latest values)
    if ((window === '1y' || window === '2y') && priceSeries.length > 0) {
      const weeklyBuckets = new Map<number, { time: number; close: number; latestTime: number }>();
      for (const pt of priceSeries) {
        const bucket = getWeekStartMonday(pt.time);
        const current = weeklyBuckets.get(bucket);
        if (!current || pt.time > current.latestTime) {
          weeklyBuckets.set(bucket, {
            time: bucket,
            close: pt.close,
            latestTime: pt.time
          });
        }
      }
      priceSeries = Array.from(weeklyBuckets.values())
        .sort((a, b) => a.time - b.time)
        .map(b => ({ time: b.time, close: b.close }));
    }
    
    // Get company name from:
    // 1. Selected ticker market data (if available - most reliable)
    //    Single ticker response: data is TickerMarketData object with company_name directly on it
    // 2. Fallback to ticker symbol
    const marketData = selectedTickerMarketData?.data as any;
    // Single ticker response structure: data is TickerMarketData object (company_name directly on data)
    const marketCompanyName = marketData?.company_name;
    const name = marketCompanyName || selectedTicker;
    
    return { priceSeries, name };
  }, [selectedTicker, tickerChartResponse, isLoadingTickerChart, timeWindowKey, selectedTickerMarketData]);

  // Trading activities
  const redisTradingActivities = useMemo(
    () => data ? selectActivityEvents(data) : {},
    [data]
  );

  // Import the helper function from PortfolioPage (simplified version)
  // Note: This should ideally be moved to a shared utility file
  const convertRedisActivitiesToHookFormat = useCallback((
    redisActivities: any,
    accountId: string | null,
    ticker: string | null,
    timeWindow: string
  ): any => {
    const result: any = {};
    if (!redisActivities || typeof redisActivities !== 'object') return result;
    
    // Helper for weekly bucketing (same as PortfolioPage)
    const toWeekBucketIso = (tsMs: number): string => {
      const mondayMs = getWeekStartMonday(tsMs);
      const monday20UTC = new Date(mondayMs);
      monday20UTC.setUTCHours(20, 0, 0, 0);
      return monday20UTC.toISOString().split('T')[0];
    };
    
    const accountIds = accountId ? [accountId] : Object.keys(redisActivities);
    
    for (const acctId of accountIds) {
      const accountEvents = redisActivities[acctId];
      if (!accountEvents || typeof accountEvents !== 'object') continue;
      
      const tickers = ticker ? [ticker.toUpperCase()] : Object.keys(accountEvents);
      
      for (const tkr of tickers) {
        const events = accountEvents[tkr];
        if (!Array.isArray(events)) continue;
        
        for (const ev of events) {
          let ms: number;
          if (typeof ev.date === 'number') {
            ms = ev.date < 1e12 ? ev.date * 1000 : ev.date;
          } else if (typeof ev.date === 'string') {
            ms = new Date(ev.date).getTime();
          } else if (ev.timestamp) {
            ms = typeof ev.timestamp === 'number' 
              ? (ev.timestamp < 1e12 ? ev.timestamp * 1000 : ev.timestamp)
              : new Date(ev.timestamp).getTime();
          } else {
            continue;
          }
          
          if (!Number.isFinite(ms)) continue;
          
          const iso = new Date(ms).toISOString();
          const dateOnly = iso.split('T')[0];
          const key = (timeWindow === '1y' || timeWindow === '2y') 
            ? toWeekBucketIso(ms)
            : dateOnly;
          
          if (!result[key]) {
            result[key] = { hasEntry: false, hasExit: false, activities: [] };
          }
          
          const et = String(ev.event_type || '').toUpperCase();
          const intent = (ev.intent || '').toString();
          const isShort = intent === 'Short';
          
          // Determine instrument type (same logic as PortfolioPage)
          const instrument = (() => {
            const instr = String(ev.instrument || '').toUpperCase();
            if (instr === 'CASH' || instr === 'INTEREST') {
              return instr;
            } else if (instr === 'DIVIDEND') {
              return 'DIVIDEND';
            } else if (instr === 'OPTION' || (et.startsWith('CALL') || et.startsWith('PUT'))) {
              return 'OPTION';
            } else {
              return 'EQUITY';
            }
          })();
          
          const activity: TradingActivity = {
            date: dateOnly,
            timestamp: iso,
            ticker: tkr,
            action: (et === 'SELL' || et === 'BUY_TO_COVER' || et === 'CALL_SELL' || et === 'PUT_SELL') ? 'SELL' : 'BUY',
            quantity: Math.abs(ev.shares || ev.quantity || ev.contracts || 0),
            price: ev.trade_price || ev.price || 0,
            account_id: acctId,
            isShort,
            instrument: instrument as 'EQUITY' | 'OPTION' | 'DIVIDEND' | 'CASH' | 'INTEREST',
            optionType: et.startsWith('CALL') ? 'CALL' : (et.startsWith('PUT') ? 'PUT' : undefined),
            optionSide: (et.endsWith('SELL') ? 'SELL' : (et.startsWith('PUT') || et.startsWith('CALL') ? 'BUY' : undefined)),
            event_type: et,
            intent: intent,
            split_affected: ev.split_affected === true || ev.split_affected === 'true', // Include split_affected flag from cache
          };
          
          result[key].activities.push(activity);
          
          // Compute hasEntry/hasExit (only for EQUITY, same as PortfolioPage)
          if (instrument === 'EQUITY') {
            if (et === 'BUY' || et === 'SHORT_SELL') result[key].hasEntry = true;
            if (et === 'SELL' || et === 'BUY_TO_COVER') result[key].hasExit = true;
          }
        }
      }
    }
    
    return result;
  }, []);

  const portfolioActivitiesRaw = useMemo(
    () => convertRedisActivitiesToHookFormat(redisTradingActivities, null, null, timeWindowKey),
    [redisTradingActivities, timeWindowKey, convertRedisActivitiesToHookFormat]
  );

  const accountActivities = useMemo(
    () => convertRedisActivitiesToHookFormat(redisTradingActivities, selectedAccountId, null, timeWindowKey),
    [redisTradingActivities, selectedAccountId, timeWindowKey, convertRedisActivitiesToHookFormat]
  );

  const tickerActivities = useMemo(
    () => convertRedisActivitiesToHookFormat(redisTradingActivities, selectedAccountId, selectedTicker, timeWindowKey),
    [redisTradingActivities, selectedAccountId, selectedTicker, timeWindowKey, convertRedisActivitiesToHookFormat]
  );
  
  // Determine chart type and data based on selections (Gold/Silver tiers only)
  const chartType = useMemo(() => {
    // Only Gold (TIER_1) can see tickers, Silver (TIER_2) can see accounts
    const viewTier = friendProfile ? friendshipTierToViewTier(friendProfile.tierFromFriendToMe) : 'basic';
    if (viewTier === 'tier1' && selectedTicker) return 'ticker';
    if ((viewTier === 'tier1' || viewTier === 'tier2') && selectedAccountId) return 'account';
    return 'portfolio';
  }, [selectedTicker, selectedAccountId, friendProfile]);

  // Format chart data based on type
  const chartData = useMemo(() => {
    if (!visibilityConfig.showChart) return [];
    
    if (chartType === 'ticker') {
      return tickerChart.priceSeries.map((p) => ({ time: p.time, close: p.close }));
    } else if (chartType === 'account') {
      return accountChart.series.map((p) => ({ time: p.time, percent: p.percent }));
    } else {
      return portfolioChart.series.map((p) => ({ time: p.time, percent: p.percent }));
    }
  }, [chartType, tickerChart, accountChart, portfolioChart, visibilityConfig.showChart]);
  
  // Compute loading state for chart based on chart type
  // For ticker charts, check lazy-loading state; for account/portfolio, use dashboard fetch state
  const isChartFetching = useMemo(() => {
    if (chartType === 'ticker') {
      // Ticker charts use lazy-loaded endpoints, so check their loading state
      return isLoadingTickerChart;
    } else {
      // Account and portfolio charts come from dashboard data
      return dashboardData.isFetching;
    }
  }, [chartType, isLoadingTickerChart, dashboardData.isFetching]);
  
  // Trading activities for chart
  const portfolioActivities = useMemo(() => {
    if (!visibilityConfig.showChart || visibilityConfig.hideTradingActivityIcons) {
      return {};
    }
    return portfolioActivitiesRaw;
  }, [portfolioActivitiesRaw, visibilityConfig.showChart, visibilityConfig.hideTradingActivityIcons]);

  const accountActivitiesFiltered = useMemo(() => {
    if (!visibilityConfig.showChart || visibilityConfig.hideTradingActivityIcons || !selectedAccountId) {
      return {};
    }
    return accountActivities;
  }, [accountActivities, visibilityConfig.showChart, visibilityConfig.hideTradingActivityIcons, selectedAccountId]);

  const tickerActivitiesFiltered = useMemo(() => {
    if (!visibilityConfig.showChart || visibilityConfig.hideTradingActivityIcons || !selectedAccountId || !selectedTicker) {
      return {};
    }
    return tickerActivities;
  }, [tickerActivities, visibilityConfig.showChart, visibilityConfig.hideTradingActivityIcons, selectedAccountId, selectedTicker]);

  // Find conversation for this friend
  const conversation = friendProfile ? conversations.find(c => c.friendUserId === friendProfile.friendId) : null;
  const hasUnreadMessages = conversation && conversation.unreadCount > 0;

  // Handle tier tooltip on hover with 0.5s delay
  const handleStarMouseEnter = () => {
    tierTooltipTimeoutRef.current = setTimeout(() => {
      setShowTierTooltip(true);
    }, 500);
  };

  const handleStarMouseLeave = () => {
    if (tierTooltipTimeoutRef.current) {
      clearTimeout(tierTooltipTimeoutRef.current);
      tierTooltipTimeoutRef.current = null;
    }
    setShowTierTooltip(false);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tierTooltipTimeoutRef.current) {
        clearTimeout(tierTooltipTimeoutRef.current);
      }
    };
  }, []);

  // Handle message icon click
  const handleMessageClick = async () => {
    if (!friendProfile) return;
    
    try {
      // Navigate to chat page with friendId query param
      // ChatPage will handle opening the conversation and showing/hiding the list appropriately
      navigate(`/chat?friendId=${friendProfile.friendId}`);
    } catch (err) {
      console.error('Failed to open chat:', err);
    }
  };

  // Format time for chart axis
  const formatTime = useCallback(
    (ms: number) => {
      const d = new Date(ms);
      if (timeWindowKey === '1m' || timeWindowKey === '3m') {
        return `${d.getMonth() + 1}/${d.getDate()}`;
      }
      if (timeWindowKey === '6m') {
        return `${d.getMonth() + 1}/${d.getDate()}`;
      }
      return `${d.getMonth() + 1}/${d.getDate()}`;
    },
    [timeWindowKey]
  );

  // Grouping for trading activities
  const groupingMs = (timeWindowKey === '1y' || timeWindowKey === '2y') ? 7 * 24 * 60 * 60 * 1000 : 0;

  // Handle activity marker click
  const handleActivityMarkerClick = useCallback((date: string, activities: TradingActivity[]) => {
    setSelectedActivityDate(date);
    setSelectedDayActivities(activities);
    setIsActivityModalOpen(true);
  }, []);

  const handleCloseActivityModal = useCallback(() => {
    setIsActivityModalOpen(false);
    setSelectedDayActivities([]);
    setSelectedActivityDate('');
  }, []);

  // Selection handlers for Gold/Silver tiers
  const handleSelectTicker = useCallback(
    (symbol: string) => {
      if (selectedTicker === symbol) {
        setSelectedTicker(null);
        setSelectedAccountId(null);
      } else {
        setSelectedTicker(symbol);
        setSelectedAccountId(null);
      }
    },
    [selectedTicker]
  );

  const handleSelectAccountTicker = useCallback(
    (acctId: string, ticker: string) => {
      if (selectedTicker === ticker && selectedAccountId === acctId) {
        setSelectedTicker(null);
        setSelectedAccountId(null);
      } else {
        setSelectedTicker(ticker);
        setSelectedAccountId(acctId);
      }
    },
    [selectedTicker, selectedAccountId]
  );

  const handleSelectAccount = useCallback(
    (acctId: string) => {
      if (selectedAccountId === acctId && !selectedTicker) {
        setSelectedAccountId(null);
      } else {
        setSelectedAccountId(acctId);
        setSelectedTicker(null);
      }
    },
    [selectedAccountId, selectedTicker]
  );

  const handleToggleAccountExpansion = useCallback(
    (accountId: string) => {
      // Only allow expansion for Gold tier (TIER_1)
      const viewTier = friendProfile ? friendshipTierToViewTier(friendProfile.tierFromFriendToMe) : 'basic';
      if (viewTier !== 'tier1') return;
      
      setExpandedAccounts(prevExpanded => {
        const newExpanded = new Set(prevExpanded);
        if (newExpanded.has(accountId)) {
          newExpanded.delete(accountId);
        } else {
          newExpanded.add(accountId);
        }
        return newExpanded;
      });
    },
    [friendProfile]
  );

  // Get investments snapshot for account names (needed for chart title)
  const investmentsSnapshot = useMemo(() => {
    if (!data) return { accounts: [] };
    return selectInvestmentsTable(data, timeWindowKey);
  }, [data, timeWindowKey]);

  // Chart title - use friend's first name and percentage change
  const chartTitle = useMemo(() => {
    if (!friendProfile) return 'Portfolio Performance';
    const firstName = friendProfile.name.split(' ')[0];
    
    if (chartType === 'ticker' && selectedTicker) {
      const name = tickerChart.name || selectedTicker;
      // Get price from market data (most reliable) or fallback to last point in chart series
      const marketData = selectedTickerMarketData?.data as any;
      const marketPrice = marketData?.last_price;
      const chartPrice = tickerChart.priceSeries.length
        ? tickerChart.priceSeries[tickerChart.priceSeries.length - 1].close
        : null;
      const price = typeof marketPrice === 'number' && !isNaN(marketPrice) 
        ? marketPrice 
        : (typeof chartPrice === 'number' && !isNaN(chartPrice) ? chartPrice : null);
      const priceText = typeof price === 'number' ? ` - $${price.toFixed(2)}` : '';
      return `${name} (${selectedTicker})${priceText}`;
    }
    
    if (chartType === 'account' && selectedAccountId) {
      const account = investmentsSnapshot.accounts.find((acc: any) => acc.accountId === selectedAccountId);
      const accountName = account?.accountName || 'Account';
      const pct = accountChart.headerPct;
      const pctText = typeof pct === 'number' ? ` (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)` : '';
      return `${accountName}${pctText}`;
    }
    
    const pct = portfolioChart.headerPct;
    const pctText = typeof pct === 'number' ? ` (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)` : '';
    return `${firstName}'s Portfolio${pctText}`;
  }, [friendProfile, chartType, selectedTicker, selectedAccountId, tickerChart, accountChart, portfolioChart, investmentsSnapshot, selectedTickerMarketData]);

  const shouldShowAnyCards =
    visibilityConfig.showPortfolioSummary ||
    visibilityConfig.showChart ||
    visibilityConfig.showInvestmentTable ||
    visibilityConfig.showTopMovers;

  const renderGoldLayout = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-2">
        <PortfolioSummaryCard
          className="h-full"
          onEditClick={() => {}}
          hideDollarSection
        />
      </div>
      <div>
        <TopMoversCard
          className="h-full"
          selectedTicker={selectedTicker}
          onSelectTicker={handleSelectTicker}
          hideShowRealizedGains={visibilityConfig.hideShowRealizedGains}
        />
      </div>
      <div className="md:col-span-2">
        <PerformanceChartCard
          className="h-full"
          chartType={chartType}
          chartData={chartData}
          title={chartTitle}
          selectedTicker={selectedTicker}
          selectedAccountId={selectedAccountId}
          timeWindowKey={timeWindowKey}
          view="pct"
          portfolioActivities={visibilityConfig.hideTradingActivityIcons ? {} : portfolioActivities}
          accountActivities={visibilityConfig.hideTradingActivityIcons ? {} : accountActivitiesFiltered}
          tickerActivities={visibilityConfig.hideTradingActivityIcons ? {} : tickerActivitiesFiltered}
          groupingMs={groupingMs}
          onActivityMarkerClick={handleActivityMarkerClick}
          formatTime={formatTime}
          isFetching={isChartFetching}
          hideToggle={visibilityConfig.hideToggle}
        />
      </div>
      <div>
        <InvestmentTableCard
          className="h-full"
          selectedTicker={selectedTicker}
          selectedAccountId={selectedAccountId}
          expandedAccounts={expandedAccounts}
          onSelectAccountTicker={handleSelectAccountTicker}
          onSelectAccount={handleSelectAccount}
          onToggleAccountExpansion={handleToggleAccountExpansion}
          onEditPortfolio={() => {}}
          hideShares={visibilityConfig.hideShares}
          hideShowRealizedGains={visibilityConfig.hideShowRealizedGains}
          enableAccountExpansion={visibilityConfig.enableAccountExpansion}
          showMyTotalText={visibilityConfig.showMyTotalText}
          hideEditButton
        />
      </div>
    </div>
  );

  const renderStandardLayout = () => {
    if (!shouldShowAnyCards) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>
            You have Basic tier access with this friend. You can't see one another's portfolio, but you can chat{' '}
            <button
              onClick={handleMessageClick}
              className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
            >
              here
            </button>
            .
          </p>
        </div>
      );
    }

    return (
      <div
        className={`grid gap-4 ${
          visibilityConfig.chartAndTableLayout === 'fullWidth'
            ? 'grid-cols-1'
            : visibilityConfig.chartAndTableLayout === 'sideBySide'
            ? 'grid-cols-1 md:grid-cols-3'
            : 'grid-cols-1 md:grid-cols-3'
        }`}
      >
        {visibilityConfig.showPortfolioSummary && (
          <div className={visibilityConfig.portfolioSummaryFullWidth ? 'md:col-span-3' : ''}>
            <PortfolioSummaryCard
              className="h-full"
              onEditClick={() => {}}
              hideDollarSection
            />
          </div>
        )}

        {visibilityConfig.showTopMovers && (
          <div className={visibilityConfig.chartAndTableLayout === 'sideBySide' ? 'md:col-span-1' : ''}>
            <TopMoversCard
              className="h-full"
              selectedTicker={selectedTicker}
              onSelectTicker={handleSelectTicker}
              hideShowRealizedGains={visibilityConfig.hideShowRealizedGains}
            />
          </div>
        )}

        {visibilityConfig.showChart && (
          <div
            className={
              visibilityConfig.chartAndTableLayout === 'sideBySide'
                ? 'md:col-span-2'
                : visibilityConfig.chartAndTableLayout === 'fullWidth'
                ? 'md:col-span-3'
                : ''
            }
          >
            <PerformanceChartCard
              className="h-full"
              chartType={chartType}
              chartData={chartData}
              title={chartTitle}
              selectedTicker={selectedTicker}
              selectedAccountId={selectedAccountId}
              timeWindowKey={timeWindowKey}
              view="pct"
              portfolioActivities={visibilityConfig.hideTradingActivityIcons ? {} : portfolioActivities}
              accountActivities={visibilityConfig.hideTradingActivityIcons ? {} : accountActivitiesFiltered}
              tickerActivities={visibilityConfig.hideTradingActivityIcons ? {} : tickerActivitiesFiltered}
              groupingMs={groupingMs}
              onActivityMarkerClick={handleActivityMarkerClick}
              formatTime={formatTime}
              isFetching={isChartFetching}
              hideToggle={visibilityConfig.hideToggle}
            />
          </div>
        )}

        {visibilityConfig.showInvestmentTable && (
          <div className={visibilityConfig.chartAndTableLayout === 'sideBySide' ? 'md:col-span-1' : ''}>
            <InvestmentTableCard
              className="h-full"
              selectedTicker={selectedTicker}
              selectedAccountId={selectedAccountId}
              expandedAccounts={expandedAccounts}
              onSelectAccountTicker={handleSelectAccountTicker}
              onSelectAccount={handleSelectAccount}
              onToggleAccountExpansion={handleToggleAccountExpansion}
              onEditPortfolio={() => {}}
              hideShares={visibilityConfig.hideShares}
              hideShowRealizedGains={visibilityConfig.hideShowRealizedGains}
              enableAccountExpansion={visibilityConfig.enableAccountExpansion}
              showMyTotalText={visibilityConfig.showMyTotalText}
              hideEditButton
            />
          </div>
        )}
      </div>
    );
  };

  const renderCardsSection = () => (isGoldTier ? renderGoldLayout() : renderStandardLayout());

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  // Not in friends list: try to load user profile (non-friend view)
  if (!friendProfile) {
    if (userProfileLoading) {
      return (
        <div className="flex justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      );
    }
    if (!userProfile) {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-center text-red-700">
            User not found or you don't have access.
          </div>
        </div>
      );
    }
    // Non-friend profile: show image, note, Connect button
    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center -mt-4 mb-2">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <button
              type="button"
              onClick={() => userProfile.profilePictureUrl && setIsImageViewerOpen(true)}
              className={`flex-shrink-0 rounded-full ${userProfile.profilePictureUrl ? 'cursor-pointer ring-2 ring-transparent hover:ring-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500' : ''}`}
            >
              {userProfile.profilePictureUrl ? (
                <img
                  className="h-10 w-10 rounded-full object-cover"
                  src={userProfile.profilePictureUrl}
                  alt=""
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center font-medium text-lg text-gray-500">
                  {userProfile.name.charAt(0).toUpperCase()}
                </div>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2">
                <h1 className="text-base font-semibold text-gray-900 truncate">{userProfile.name}</h1>
                {userProfile.isFriend ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Friend
                  </span>
                ) : userProfile.hasOutgoingRequest ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                ) : userProfile.hasIncomingRequest ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Requested
                  </span>
                ) : (
                  <button
                    onClick={() => setIsConnectModalOpen(true)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Connect
                  </button>
                )}
              </div>
              {userProfile.username && (
                <p className="text-sm text-gray-500 truncate">@{userProfile.username}</p>
              )}
            </div>
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-gray-600">
          {userProfile.isFriend
            ? "You are friends with this user."
            : "You are not friends with this user."}
        </div>

        <ConnectModal
          isOpen={isConnectModalOpen}
          onClose={() => setIsConnectModalOpen(false)}
          onConnect={async (tier, note) => {
            if (!session?.access_token) return;
            await sendFriendRequest(session.access_token, { toUserId: userProfile.userId, tier, note });
            queryClient.invalidateQueries({ queryKey: ['userProfile', friendId] });
            setIsConnectModalOpen(false);
          }}
          recipientName={userProfile.name}
        />

        {userProfile.profilePictureUrl && (
          <ImageViewer
            isOpen={isImageViewerOpen}
            onClose={() => setIsImageViewerOpen(false)}
            images={[{ src: userProfile.profilePictureUrl, alt: userProfile.name }]}
            currentIndex={0}
            onNavigate={() => {}}
            variant="profile"
          />
        )}
      </div>
    );
  }

  return (
      <div ref={contentRef} className="space-y-3">
        {/* Friend Header and Time Window Selector */}
        <div ref={headerRef} className="flex justify-between items-center -mt-4 mb-2">
          {/* Friend Profile */}
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <button
              type="button"
              onClick={() => friendProfile.profilePictureUrl && setIsImageViewerOpen(true)}
              className={`flex-shrink-0 rounded-full ${friendProfile.profilePictureUrl ? 'cursor-pointer ring-2 ring-transparent hover:ring-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500' : 'cursor-default'}`}
            >
              {friendProfile.profilePictureUrl ? (
                <img
                  className="h-10 w-10 rounded-full object-cover"
                  src={friendProfile.profilePictureUrl}
                  alt=""
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center font-medium text-lg text-gray-500">
                  {friendProfile.name.charAt(0).toUpperCase()}
                </div>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h1 className="text-base font-semibold text-gray-900 truncate">{friendProfile.name}</h1>
                <div 
                  className="relative flex-shrink-0"
                  onMouseEnter={handleStarMouseEnter}
                  onMouseLeave={handleStarMouseLeave}
                >
                  <span 
                    ref={starRef}
                    className={`${TIER_COLORS[friendProfile.tierFromFriendToMe] || ''} text-base cursor-pointer`}
                    style={friendProfile.tierFromFriendToMe === 'TIER_3' ? { color: '#A75217' } : undefined}
                  >
                    ★
                  </span>
                  
                  {/* Tooltip */}
                  {showTierTooltip && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                      {TIER_LABELS[friendProfile.tierFromFriendToMe]}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <div className="border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {friendProfile.username && (
                <p className="text-sm text-gray-500 truncate">@{friendProfile.username}</p>
              )}
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button 
                className="relative inline-flex items-center justify-center p-2 border border-blue-600 rounded-full text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                onClick={handleMessageClick}
                title="Message"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {hasUnreadMessages && (
                  <span className="absolute top-0 right-0 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"></span>
                )}
              </button>
            </div>
          </div>

          {/* Time Window Selector - Only show if tier allows */}
          {visibilityConfig.showTimePicker && (
            <div className="relative flex-shrink-0 ml-2" ref={timeWindowRef}>
              <button
                onClick={() => setIsTimeWindowOpen(!isTimeWindowOpen)}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-blue-500 text-white rounded-full text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden sm:inline">{selectedTimeWindow.label}</span>
                <span className="sm:hidden">{selectedTimeWindow.key}</span>
              </button>

              {/* Dropdown */}
              {isTimeWindowOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 top-full">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    {timeWindowOptions.map((option) => (
                      <button
                        key={option.key}
                        onClick={() => {
                          setSelectedTimeWindow(option);
                          setIsTimeWindowOpen(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          selectedTimeWindow.key === option.key
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700'
                        } hover:bg-gray-100 hover:text-gray-900`}
                        role="menuitem"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky Time Window Selector (appears when scrolled) */}
        {showStickyTimeWindow && visibilityConfig.showTimePicker && (
          <div 
            className="fixed top-4 z-50 transition-all duration-200"
            style={{ right: `${stickyRightOffset}px` }}
          >
            <div className="relative">
              <button
                onClick={() => setIsStickyTimeWindowOpen(!isStickyTimeWindowOpen)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-full text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{selectedTimeWindow.label}</span>
              </button>

              {/* Dropdown */}
              {isStickyTimeWindowOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 top-full">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    {timeWindowOptions.map((option) => (
                      <button
                        key={option.key}
                        onClick={() => {
                          setSelectedTimeWindow(option);
                          setIsStickyTimeWindowOpen(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          selectedTimeWindow.key === option.key
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700'
                        } hover:bg-gray-100 hover:text-gray-900`}
                        role="menuitem"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Refreshing indicator: show while fetching or when backend signals refresh in progress (e.g. Case D background recalc) */}
        {(dashboardData.isFetching || dashboardData.meta?.refresh_needed === true) && (
          <div className="fixed top-20 left-4 z-50 flex items-center space-x-2 bg-white px-3 py-2 rounded-md shadow-md border">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            <span className="text-sm text-gray-600">Refreshing portfolio data…</span>
          </div>
        )}

        <DashboardProvider value={dashboardData}>
          {renderCardsSection()}
        </DashboardProvider>

        {/* Trading Activity Modal */}
        <TradingActivityModal
          isOpen={isActivityModalOpen}
          onClose={handleCloseActivityModal}
          activities={selectedDayActivities}
          date={selectedActivityDate}
          isWeeklyView={timeWindowKey === '1y' || timeWindowKey === '2y'}
          friendView={visibilityConfig.tradingActivityFriendView}
        />

        {/* Profile picture viewer */}
        {friendProfile.profilePictureUrl && (
          <ImageViewer
            isOpen={isImageViewerOpen}
            onClose={() => setIsImageViewerOpen(false)}
            images={[{ src: friendProfile.profilePictureUrl, alt: friendProfile.name }]}
            currentIndex={0}
            onNavigate={() => {}}
            variant="profile"
          />
        )}
      </div>
  );
};
